const MS_MIN = 60_000;
const MS_HOUR = 60 * MS_MIN;
const MS_DAY = 24 * MS_HOUR;

const EN = {
  now: 'just now',
  min: (n) => (n === 1 ? '1 min ago' : `${n} min ago`),
  hour: (n) => (n === 1 ? '1 hour ago' : `${n} hours ago`),
  day: (n) => (n === 1 ? '1 day ago' : `${n} days ago`),
  week: (n) => (n === 1 ? '1 week ago' : `${n} weeks ago`),
  month: (n) => (n <= 1 ? '1 month ago' : `${n} months ago`),
  year: (n) => (n <= 1 ? '1 year ago' : `${n} years ago`),
};

const rel = (t, key, count) =>
  t ? t(`integrations.audit.relative.${key}`, { count }) : null;

/**
 * Formats an ISO/Date (or already-relative string) for chips and tables.
 * Pass `t` from `useTranslation('admin-studio')` so copy follows the UI locale
 * (CT-4995). Without `t`, English fallbacks keep mapper/unit tests stable.
 *
 * @param {string|Date|null|undefined} input
 * @param {Function|number} [tOrNow]
 * @param {number} [nowMs]
 * @returns {string}
 */
export function formatRelativeTime(input, tOrNow, nowMs) {
  const t = typeof tOrNow === 'function' ? tOrNow : null;
  const now = typeof tOrNow === 'number' ? tOrNow : (nowMs ?? Date.now());

  if (input == null || input === '') return '';

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (/ago$/i.test(trimmed) || /^just now$/i.test(trimmed) || /^ahora mismo$/i.test(trimmed)) {
      return trimmed;
    }
  }

  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return typeof input === 'string' ? input : '';

  const diffMs = Math.max(0, now - date.getTime());
  const mins = Math.floor(diffMs / MS_MIN);
  const hours = Math.floor(diffMs / MS_HOUR);
  const days = Math.floor(diffMs / MS_DAY);

  if (mins < 1) return rel(t, 'now') ?? EN.now;
  if (mins < 60) return rel(t, 'min', mins) ?? EN.min(mins);
  if (hours < 24) return rel(t, hours === 1 ? 'hour' : 'hours', hours) ?? EN.hour(hours);
  if (days < 7) return rel(t, days === 1 ? 'day' : 'days', days) ?? EN.day(days);

  if (days < 30) {
    const weeks = Math.max(1, Math.floor(days / 7));
    return rel(t, weeks === 1 ? 'week' : 'weeks', weeks) ?? EN.week(weeks);
  }

  if (days < 365) {
    const months = Math.min(11, Math.max(1, Math.floor(days / 30)));
    return rel(t, months <= 1 ? 'month' : 'months', months) ?? EN.month(months);
  }

  const years = Math.max(1, Math.floor(days / 365));
  return rel(t, years <= 1 ? 'year' : 'years', years) ?? EN.year(years);
}
